/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Component, Fragment, type ReactElement, type ReactNode, Suspense, use, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeferredRender } from '@/hooks/use-deferred-render';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { useShowPickupAvailable } from './use-pickup-filter';
import type { ShopperSearch } from '@/scapi';
import DynamicImageProvider from '@/providers/dynamic-image';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import { ProductTileSkeleton } from '@/components/category-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { Region } from '@/components/region';
import type { PageWithComponentData } from '@/lib/page-designer/pageLoader';
import { IMAGE_PRESETS } from '@/lib/image-presets';

type ProductSearchHit = ShopperSearch.schemas['ProductSearchHit'];

type PromoSpotPage = Promise<PageWithComponentData | null> | PageWithComponentData | null | undefined;

const PROMO_SPOT_1_AFTER_TILE = 8;
const PROMO_SPOT_1_MIN_TILES = 8;

// Promo 2 is *inserted* after tile 18 so it lands at md+ row 6 cols 3-4 / row 7 cols 3-4 — sitting
// aligned with tile 18 (same row) and tile 20 (next row, col 2). It only *renders* when total tiles
// >= 20 so tile 20 actually fills the cell to its left and there's no empty gap. Tiles 21-24 (if
// present) flow into row 8 below.
const PROMO_SPOT_2_AFTER_TILE = 18;
const PROMO_SPOT_2_MIN_TILES = 20;

const PROMO_SPOT_1_REGION_ID = 'inGridPromoSpot1';
const PROMO_SPOT_2_REGION_ID = 'inGridPromoSpot2';

// Promo 1 auto-places at columns 1-2. Promo 2 is pinned to columns 3-4 at md+ where the grid is 4-wide;
// at sm (3 cols) and base (2 cols) there's no 3rd/4th column distinction, so it auto-places as usual.
const PROMO_SPOT_1_CLASSES = 'col-span-2 row-span-2';
const PROMO_SPOT_2_CLASSES = 'col-span-2 row-span-2 md:col-start-3';

/**
 * Resolves the page promise (suspending until ready) and renders the wrapper only when the promo region exists
 * and has at least one component. This avoids leaving an empty 2x2 hole in the grid when the author has not
 * yet populated the promo region in Page Designer.
 */
function PromoSpotInner({
    page,
    regionId,
    className,
}: {
    page: Exclude<PromoSpotPage, undefined | null>;
    regionId: string;
    className: string;
}) {
    const resolved = use(Promise.resolve(page));
    const region = resolved?.regions?.find((r) => r.id === regionId);
    if (!region?.components?.length) return null;
    return (
        <div className={className}>
            <Region page={resolved} regionId={regionId} />
        </div>
    );
}

function PromoSpot({ page, regionId, className }: { page: PromoSpotPage; regionId: string; className: string }) {
    if (!page) return null;
    return (
        <Suspense fallback={null}>
            <PromoSpotInner page={page} regionId={regionId} className={className} />
        </Suspense>
    );
}

// Responsive column counts (Figma 12165:93205). The outer page layout at md+ is a CSS
// grid with equal-width tracks (3 tracks at md, 4 tracks at lg+); the filter occupies one
// of those tracks when open. ProductGrid renders products inside the remaining tracks:
//   - base / sm (mobile):  2 cols
//   - md (tablet):  open → 2 cols (inside col-span-2)   |  closed → 3 cols (inside col-span-3)
//   - lg+ (desktop): open → 3 cols (inside col-span-3)   |  closed → 4 cols (inside col-span-4)
//
// Per-column width at md+ is the SAME whether the filter is open or closed (each track is
// 1/N of the container minus padding/gaps), because the inner grid simply fills its span
// with the same number of tracks as the span. That's the whole point of the equal-column
// layout — opening the filter doesn't change product tile dimensions.
//
// Tailwind classes are static literals so the JIT/AOT compiler can extract them.
const GRID_COLS_WITH_REFINEMENTS = 'grid-cols-2 lg:grid-cols-3';
const GRID_COLS_WITHOUT_REFINEMENTS = 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

// Responsive image-width hints are defined centrally in IMAGE_PRESETS (src/lib/image-presets.ts):
// - TILE:                  refinements panel closed — 2 cols → 3 cols → 4 cols
// - TILE_WITH_REFINEMENTS: refinements panel open  — 2 cols → 3 cols inside the sidebar-reduced container

// Scopes non-critical-product failures to the below-the-fold section: when the deferred promise
// rejects, `use()` throws here and we render the caller-supplied `errorElement` instead of crashing
// the whole grid. Critical (above-the-fold) tiles render outside this boundary and stay visible.
class NonCriticalErrorBoundary extends Component<
    { fallback?: ReactElement; children: ReactNode },
    { hasError: boolean }
> {
    state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback ?? null;
        }
        return this.props.children;
    }
}

function NoProductsMessage({ criticalSize, nonCriticalSize }: { criticalSize: number; nonCriticalSize: number }) {
    const { t } = useTranslation('common');

    if (criticalSize > 0 || nonCriticalSize > 0) {
        return null;
    }
    return (
        <div className="col-span-full text-center py-12">
            <p className="text-lg text-muted-foreground">{t('noProductsFound')}</p>
        </div>
    );
}

function NonCriticalContent({
    nonCritical,
    criticalSize,
    responsiveImageWidths,
    handleProductClick,
    topCategoryName,
    page,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable,
}: {
    nonCritical: Promise<ProductSearchHit[]>;
    criticalSize: number;
    responsiveImageWidths: string[];
    handleProductClick?: (product: ProductSearchHit) => void;
    topCategoryName?: string;
    page?: PromoSpotPage;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}) {
    const products = use(nonCritical);
    // Total tiles actually rendered. Promos only display when enough preceding tiles exist.
    const totalTiles = criticalSize + products.length;
    return (
        <DynamicImageProvider value={{ widths: responsiveImageWidths }}>
            {products.map((product, i) => {
                const tileIndex = criticalSize + i + 1; // 1-based absolute tile index
                return (
                    <Fragment key={product.productId}>
                        <ProductTile
                            product={product}
                            handleProductClick={handleProductClick}
                            showNavigationArrows
                            topCategoryName={topCategoryName}
                            // @sfdc-extension-line SFDC_EXT_BOPIS
                            showPickupAvailable={showPickupAvailable}
                        />
                        {tileIndex === PROMO_SPOT_1_AFTER_TILE && totalTiles >= PROMO_SPOT_1_MIN_TILES && (
                            <PromoSpot page={page} regionId={PROMO_SPOT_1_REGION_ID} className={PROMO_SPOT_1_CLASSES} />
                        )}
                        {tileIndex === PROMO_SPOT_2_AFTER_TILE && totalTiles >= PROMO_SPOT_2_MIN_TILES && (
                            <PromoSpot page={page} regionId={PROMO_SPOT_2_REGION_ID} className={PROMO_SPOT_2_CLASSES} />
                        )}
                    </Fragment>
                );
            })}
            <NoProductsMessage criticalSize={criticalSize} nonCriticalSize={products.length} />
        </DynamicImageProvider>
    );
}

function ProductGridSkeleton({
    count = 8,
    hasRefinementsPanel = true,
}: {
    count?: number;
    hasRefinementsPanel?: boolean;
}) {
    const colClasses = hasRefinementsPanel ? GRID_COLS_WITH_REFINEMENTS : GRID_COLS_WITHOUT_REFINEMENTS;
    return (
        <div className={`grid ${colClasses} gap-x-4 gap-y-8`}>
            {Array.from({ length: count }, (_, index) => (
                <div key={`grid-skeleton-${index}`} className="space-y-3">
                    <Skeleton className="aspect-square w-full rounded-md" />
                    <div className="space-y-2">
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-5 w-2/3" />
                        <Skeleton className="h-5 w-20" />
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * ProductGrid wraps all tiles in a shared context provider to reduce hydration overhead. Instead of each tile
 * initializing its own hooks (navigate, config, translation, currency), the provider initializes them once and shares
 * them via context.
 *
 * Performance optimizations: The grid accepts both synchronous (critical) and asynchronous (non-critical) data as
 * input. Depending on the positioning of the grid in the DOM, this allows the consumer to influence metrics such as
 * LCP. The images associated with product items that have been marked as critical are also loaded with high priority
 * and eagerly. Non-critical tiles are deferred until an idle frame is available, reducing initial render blocking time
 * (TBT) and improving LCP by prioritizing the first batch of visible tiles.
 */
export default function ProductGrid({
    critical,
    nonCritical,
    nonCriticalCount = 0,
    hasRefinementsPanel = true,
    handleProductClick,
    topCategoryName,
    isLoading = false,
    page,
    errorElement,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable: showPickupAvailableProp,
}: {
    critical?: ProductSearchHit[];
    nonCritical?: Promise<ProductSearchHit[]>;
    nonCriticalCount?: number;
    hasRefinementsPanel?: boolean;
    handleProductClick?: (product: ProductSearchHit) => void;
    topCategoryName?: string;
    isLoading?: boolean;
    /** Rendered in place of the non-critical tiles when the deferred promise rejects. */
    errorElement?: ReactElement;
    /**
     * The Page Designer page that owns the in-grid promo regions
     * (`inGridPromoSpot1`, `inGridPromoSpot2`). Promos are only rendered
     * when the corresponding region exists on this page and has at least one component.
     */
    page?: PromoSpotPage;
    // @sfdc-extension-line SFDC_EXT_BOPIS
    showPickupAvailable?: boolean;
}): ReactElement {
    const criticalData = critical ?? [];
    const l = criticalData.length;
    const responsiveImageWidths = hasRefinementsPanel
        ? IMAGE_PRESETS.TILE_WITH_REFINEMENTS.widths
        : IMAGE_PRESETS.TILE.widths;

    // Initialize the `<DynamicImageProvider/>` behavior for the scope of this grid.
    // Out-of-the-box we make sure that the product images of all products considered critical (displayed inside a
    // `<DynamicImage/>` component) should be loaded eagerly with high priority.
    const hasSource = useCallback(() => true, []);

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const pickupFromUrl = useShowPickupAvailable();
    const showPickupAvailable = showPickupAvailableProp ?? pickupFromUrl;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    const loadingSkeletonCount = Math.max(criticalData.length + nonCriticalCount, 4);

    // Defer rendering of non-critical tiles until an idle frame is available.
    // This improves initial render performance by prioritizing the critical (above-the-fold) tiles.
    const shouldRenderNonCritical = useDeferredRender(nonCriticalCount > 0);

    if (isLoading) {
        return (
            <ProductTileProvider>
                <div data-testid="product-grid-loading-state" aria-busy>
                    <ProductGridSkeleton count={loadingSkeletonCount} hasRefinementsPanel={hasRefinementsPanel} />
                </div>
            </ProductTileProvider>
        );
    }

    const colClasses = hasRefinementsPanel ? GRID_COLS_WITH_REFINEMENTS : GRID_COLS_WITHOUT_REFINEMENTS;

    return (
        <ProductTileProvider>
            <div data-testid="product-grid" className={`grid ${colClasses} gap-x-4 gap-y-8`}>
                {l > 0 && (
                    <DynamicImageProvider value={{ hasSource, widths: responsiveImageWidths }}>
                        {criticalData.map((product, i) => {
                            const tileIndex = i + 1; // 1-based absolute tile index
                            return (
                                <Fragment key={product.productId}>
                                    <ProductTile
                                        product={product}
                                        handleProductClick={handleProductClick}
                                        showNavigationArrows
                                        topCategoryName={topCategoryName}
                                        // @sfdc-extension-line SFDC_EXT_BOPIS
                                        showPickupAvailable={showPickupAvailable}
                                    />
                                    {/* Promo only renders here when criticalSize is big enough to satisfy the threshold;
                                        otherwise insertion happens inside NonCriticalContent. */}
                                    {tileIndex === PROMO_SPOT_1_AFTER_TILE && l >= PROMO_SPOT_1_MIN_TILES && (
                                        <PromoSpot
                                            page={page}
                                            regionId={PROMO_SPOT_1_REGION_ID}
                                            className={PROMO_SPOT_1_CLASSES}
                                        />
                                    )}
                                    {tileIndex === PROMO_SPOT_2_AFTER_TILE && l >= PROMO_SPOT_2_MIN_TILES && (
                                        <PromoSpot
                                            page={page}
                                            regionId={PROMO_SPOT_2_REGION_ID}
                                            className={PROMO_SPOT_2_CLASSES}
                                        />
                                    )}
                                </Fragment>
                            );
                        })}
                    </DynamicImageProvider>
                )}
                {/*
                    Three-phase rendering strategy for non-critical content:

                    Phase 1 (Pre-Idle): nonCritical exists BUT shouldRenderNonCritical is false
                    - We have a promise with non-critical products, but idle callback hasn't triggered yet
                    - Show simple skeletons without mounting the Suspense boundary (until requestIdleCallback fires)
                    - Why: Mounting Suspense immediately would force React to process it during initial render,
                      blocking the main thread (causing TBT, delaying LCP). By deferring the Suspense boundary
                      itself, we keep the initial render minimal (just critical tiles + lightweight skeletons).

                    Phase 2 (Post-Idle, Pending): shouldRenderNonCritical is true, promise is pending
                    - Idle callback has triggered, Suspense boundary is now mounted
                    - Show skeletons via Suspense fallback while the promise resolves

                    Phase 3 (Resolved): shouldRenderNonCritical is true, promise resolved
                    - All non-critical tiles are rendered
                    - Skeletons are replaced with actual product tiles
                */}
                {nonCritical && shouldRenderNonCritical ? (
                    // Phase 2 & 3: Post-idle — mount Suspense boundary and render tiles
                    <NonCriticalErrorBoundary fallback={errorElement}>
                        <Suspense
                            fallback={Array.from({ length: nonCriticalCount }, (_, i) => (
                                <ProductTileSkeleton key={i} />
                            ))}>
                            <NonCriticalContent
                                nonCritical={nonCritical}
                                criticalSize={l}
                                responsiveImageWidths={responsiveImageWidths}
                                handleProductClick={handleProductClick}
                                topCategoryName={topCategoryName}
                                page={page}
                                // @sfdc-extension-line SFDC_EXT_BOPIS
                                showPickupAvailable={showPickupAvailable}
                            />
                        </Suspense>
                    </NonCriticalErrorBoundary>
                ) : nonCritical ? (
                    // Phase 1: Pre-idle — show lightweight skeletons before Suspense boundary mounts
                    // This prevents React from processing the Suspense boundary during initial render,
                    // reducing Total Blocking Time (TBT) and improving LCP for critical tiles.
                    Array.from({ length: nonCriticalCount }, (_, i) => <ProductTileSkeleton key={i} />)
                ) : (
                    // No non-critical promise exists — show empty state if no critical tiles either
                    <NoProductsMessage criticalSize={l} nonCriticalSize={0} />
                )}
            </div>
        </ProductTileProvider>
    );
}
